import * as THREE from 'three'
import type { ImportedPartBodyBrepBinding, ImportedPartBodyTopologyAsset, ImportedTopologySource } from '../../imported-model/loaders'
import { parseImportedBodyEntries } from '../../imported-model/loaders/internal'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { rebaseImportedBodyOccurrenceBindingId, resolveImportedBodyDefinitionId } from '../../imported-model/utils'
import {
  ImportedModelTopologyRepository,
  registerImportedModelTopologyRepository,
  resolveImportedModelTopologyRepositoryAtRoot,
} from '../../imported-model/topology/repository-internal'
import type { PuppyAssemblyDocument } from './types'

/**
 * 恢复轻量引用副本的 occurrence 专属拓扑身份。
 *
 * 复制可共享几何、BRep 和拓扑快照，但不能共享 Body 的实例身份。本类消费 materialize 已写入的
 * assembly node 归属，逐 Mesh 建立绑定，避免按整包 meshId 激活时把同源副本混入同一仓库记录。
 * 新仓库完整构造后才发布；不释放父装配仓库，也不修改共享 topologySource。
 */
export class PuppyReferenceCopyTopologyRestorer {
  /** 源 meshId 到完整 Body 绑定集合的索引，避免为每个副本重复扫描整包。 */
  private readonly bindingsByMeshId = new Map<string, ImportedPartBodyBrepBinding[]>()

  /**
   * @param assembly 当前离线包已展开的装配定义。
   * @param source 普通模型导入阶段解析的共享拓扑源。
   */
  public constructor(
    private readonly assembly: PuppyAssemblyDocument,
    private readonly source?: ImportedTopologySource,
  ) {
    Object.values(source?.bodyBrepBindings ?? {}).forEach(binding => {
      const bindings = this.bindingsByMeshId.get(binding.meshId) ?? []
      bindings.push(binding)
      this.bindingsByMeshId.set(binding.meshId, bindings)
    })
  }

  /**
   * 在当前副本作用域内同步仓库、装配 Body 条目与选择身份。
   * @param root 已挂载并写入 puppy.assemblyNodeId 的副本或代表 payload。
   */
  public restore(root: THREE.Object3D): void {
    if (!this.source) return
    // materialize 会动态追加子装配节点，按本次完整定义建索引，不在每个 Mesh 上全表扫描。
    const nodeById = new Map(this.assembly.nodes.map(node => [node.nodeId, node]))
    const repository = new ImportedModelTopologyRepository(root)
    const bindingIdsByOwner = new Map<string, Set<string>>()
    const pendingMetadata: Array<() => void> = []
    const usedBindingIds = new Set<string>()
    try {
      root.traverse(object => {
        if (!(object instanceof THREE.Mesh)) return
        const meshId: unknown = object.userData?.partAssetLocal?.meshId
        const bindings = typeof meshId === 'string' ? this.bindingsByMeshId.get(meshId) : undefined
        if (!bindings?.length) return
        const owner: unknown = object.userData?.puppy?.assemblyNodeId
        if (typeof owner !== 'string' || !owner.trim()) {
          throw new Error(`引用副本拓扑缺少装配归属：mesh=${object.uuid}`)
        }
        const node = nodeById.get(owner)
        if (!node) throw new Error(`引用副本拓扑归属节点不存在：node=${owner}, mesh=${object.uuid}`)
        const assets: Record<string, ImportedPartBodyTopologyAsset> = {}
        const ownedIds = bindingIdsByOwner.get(owner) ?? new Set<string>()
        const scopedBindings = bindings.map(binding => {
          const asset = this.source!.bodyTopologyAssets?.[binding.bodyBindingId]
          if (!asset) throw new Error(`引用副本缺少 Body 拓扑资产：node=${owner}, body=${binding.bodyBindingId}`)
          const bodyBindingId = rebaseImportedBodyOccurrenceBindingId(binding.bodyBindingId, owner)
          if (usedBindingIds.has(bodyBindingId)) {
            throw new Error(`引用副本 Body 绑定重复：node=${owner}, body=${bodyBindingId}`)
          }
          usedBindingIds.add(bodyBindingId)
          ownedIds.add(bodyBindingId)
          // 只复制身份外壳；顶点、拓扑快照及几何对象 ID 保持共享。
          assets[bodyBindingId] = { ...asset, bodyBindingId, meshData: {
            ...asset.meshData, metadata: { ...asset.meshData.metadata, bodyBindingId },
          } }
          return { ...binding, bodyBindingId }
        })
        repository.registerVisibleMesh(object, scopedBindings, assets)
        bindingIdsByOwner.set(owner, ownedIds)
        const sourceEntries = parseImportedBodyEntries(object.userData?.importedPartBodyEntries)
        const bodyEntries = scopedBindings.map(binding => {
          const bodyId = binding.bodyId ?? resolveImportedBodyDefinitionId(binding.bodyBindingId)
          const entry = sourceEntries.find(candidate => candidate.bodyId === bodyId)
          return { ...entry, bodyId, bodyBindingId: binding.bodyBindingId,
            geometryObjectId: assets[binding.bodyBindingId]!.geometryObjectId,
            label: entry?.label ?? bodyId,
          }
        })
        pendingMetadata.push(() => {
          object.userData = { ...object.userData, importedPartBodyEntries: bodyEntries,
            primitiveEntityEntries: scopedBindings.flatMap((binding, index) => binding.primitiveIds.map((primitiveId, ordinal) => ({
              primitiveId, primitiveIndex: binding.primitiveIndices[ordinal] ?? 0,
              bodyId: binding.bodyId, bodyBindingId: binding.bodyBindingId, label: bodyEntries[index]!.label,
            }))),
          }
          const metadata = ImportedModelObjectMetadataService.peekMetadata(object)
          const selectionTarget = { ...metadata?.selectionTarget, assemblyNodeId: owner }
          ImportedModelObjectMetadataService.writeMetadata(object, {
            assemblyNodeId: owner, selectionTarget,
            // 后续 hierarchy 构建会提供精确的组件根代理，不能保留克隆前的源对象 UUID。
            selectionProxyObjectId: undefined,
            bodyBindingSelectionTargetsById: Object.fromEntries(scopedBindings.map(binding => [
              binding.bodyBindingId, { ...selectionTarget },
            ])),
            hierarchy: metadata?.hierarchy ? { ...metadata.hierarchy, meta: metadata.hierarchy.meta ? {
              ...metadata.hierarchy.meta, assemblyNodeId: owner, bodyEntries, bodyCount: bodyEntries.length,
              selectionObjectId: object.uuid, highlightTargets: undefined, bodyHighlightTargetsById: undefined,
            } : undefined } : undefined,
          })
        })
      })
      if (!repository.hasRecords()) {
        repository.dispose()
        return
      }
      // 所有绑定校验通过后再提交；只允许替换当前副本根自己拥有的旧仓库。
      resolveImportedModelTopologyRepositoryAtRoot(root)?.dispose()
      pendingMetadata.forEach(apply => apply())
      bindingIdsByOwner.forEach((ids, owner) => {
        const node = nodeById.get(owner)!
        node.bodyBindingIds = [...new Set([
          ...node.bodyBindingIds.map(id => rebaseImportedBodyOccurrenceBindingId(id, owner)), ...ids,
        ])]
        if (node.meta?.bodyEntries) {
          node.meta = { ...node.meta, bodyEntries: node.meta.bodyEntries.map(entry => ({ ...entry,
            bodyBindingId: entry.bodyBindingId ? rebaseImportedBodyOccurrenceBindingId(entry.bodyBindingId, owner) : undefined,
          })) }
        }
      })
      registerImportedModelTopologyRepository(root, repository)
    } catch (error) {
      repository.dispose()
      throw error
    }
  }
}
